/**
 * Data quality's part of the attention inbox (lib/inbox): open high-severity findings, for
 * people who may decide on them — acknowledge right there, or open the finding.
 */
import { Check } from 'lucide-react';
import { api, unwrap } from '@/lib/api/client';
import { can } from '@/lib/can';
import { msg, t, tp } from '@/lib/i18n';
import type { InboxSource } from '@/lib/inbox';
import { Button, toast } from '@/ui';
import { qualityKeys, useDecideFinding } from './api';
import type { FindingOut } from './types';

const QUERY = { status: ['open'], severity: ['high'], limit: 50 };

function Acknowledge({ finding }: { finding: FindingOut }) {
  const decide = useDecideFinding();
  return (
    <Button
      size="sm"
      icon={Check}
      loading={decide.isPending}
      onClick={() =>
        decide.mutate(
          { id: finding.id, verb: 'acknowledge', note: '', version: finding.version },
          {
            onSuccess: (r) => toast({ title: r.message, tone: 'success' }),
            onError: (err) => toast({ title: t('Not saved'), description: err instanceof Error ? err.message : String(err), tone: 'danger' }),
          },
        )
      }
    >
      {t('Acknowledge')}
    </Button>
  );
}

export const findingsInbox: InboxSource<{ items: FindingOut[] }> = {
  key: 'quality',
  label: msg('Data quality'),
  order: 40,
  permission: 'quality.finding.manage',
  when: (me) => can(me, 'quality.finding.read'),
  module: 'quality',
  href: '/quality/findings?severity=high',
  // Under qualityKeys.all, so deciding a finding anywhere (and quality events) refreshes it.
  query: () => ({
    queryKey: [...qualityKeys.all, 'inbox', QUERY],
    queryFn: () => unwrap(api.GET('/api/quality/findings', { params: { query: QUERY } })),
    refetchInterval: 300_000,
  }),
  items: (page) =>
    page.items.map((f) => ({
      id: `finding:${f.id}`,
      source: 'quality',
      kind: msg('Data quality'),
      tone: 'bad',
      title: f.title,
      detail: f.label || null,
      subject: f.vehicle_id ? { type: 'vehicle', id: f.vehicle_id, label: f.vehicle_name } : null,
      meta: f.occurrences > 1 ? [tp(f.occurrences, 'seen once', 'seen {count} times')] : undefined,
      at: f.last_seen,
      href: `/quality/findings/${f.id}`,
      rank: 4,
      actions: <Acknowledge finding={f} />,
    })),
};
